import { v4 as uuidv4 } from 'uuid';
import { router } from '@/app/router';
import { sentryService } from '@/services/telemetry/sentry';
import colorToRgba from '@/shared/utils/color-to-rgba';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';

export class Animations {
  TYPES = {
    PULSE_BOX: 'pulse_box',
    PULSE_TEXT: 'pulse_text',
    BUZZ: 'buzz',
    FLASH: 'flash',
    APPEAR_ARC: 'appear_arc',
  };

  ongoing = {};

  // Stable handler reference for idempotent subscribe/unsubscribe
  _onStepChanged = null;

  constructor() {
    exposeToGlobalScope(this, 'Animations');
  }

  initialize() {
    // Guard against duplicate initialization: remove previous handler first
    if (this._onStepChanged) {
      router.off(router.EVENTS.STEP_CHANGED, this._onStepChanged);
    }

    this._onStepChanged = () => {
      this.stopAllAnimations();
    };

    router.on(router.EVENTS.STEP_CHANGED, this._onStepChanged);
  }

  /**
   * Track animation performance metrics
   * @param {string} animationType - Type of animation
   * @param {number} expectedDuration - Expected duration in ms
   * @param {number} actualDuration - Actual duration in ms
   */
  _trackAnimationPerformance(animationType, expectedDuration, actualDuration) {
    try {
      const driftMs = actualDuration - expectedDuration;
      const driftPercent =
        expectedDuration > 0 ? (driftMs / expectedDuration) * 100 : 0;

      sentryService.trackMetric(
        'animation.duration_ms',
        actualDuration,
        'millisecond',
        {
          animation_type: animationType,
        }
      );

      if (Math.abs(driftPercent) > 20) {
        // Animation took 20%+ longer or shorter than expected
        sentryService.addBreadcrumb(
          'animation',
          'Animation drift detected',
          {
            animation_type: animationType,
            expected_ms: expectedDuration,
            actual_ms: Math.round(actualDuration),
            drift_percent: Math.round(driftPercent),
          },
          Math.abs(driftPercent) > 50 ? 'warning' : 'info'
        );
      }
    } catch (e) {
      // Animation tracking is non-critical
    }
  }

  /**
   * @param {HTMLElement|Object} ref - DOM element or Vue ref to animate.
   * @param {Object} [options]
   * @param {number} [options.gap] - Duration between pulsations in milliseconds.
   * @param {number} [options.count] - Number of pulsations.
   * @param {boolean} [options.infinite] - If true, the animation will repeat infinitely.
   */
  pulseBox(ref, options) {
    return this.createOngoingAnimation(
      ref,
      this.TYPES.PULSE_BOX,
      this._pulseBoxOnce,
      options
    );
  }

  /**
   * @param {HTMLElement|Object} ref - DOM element or Vue ref to animate.
   * @param {Object} [options]
   * @param {number} [options.duration] - Duration of a single pulsation in milliseconds.
   * @param {number} [options.gap] - Duration between pulsations in milliseconds.
   * @param {number} [options.scale] - Scale factor (>1) indicating how much the pulsation expands.
   * @param {number} [options.count] - Number of pulsations.
   * @param {boolean} [options.infinite] - If true, the animation will repeat infinitely.
   */
  pulseText(ref, options) {
    return this.createOngoingAnimation(
      ref,
      this.TYPES.PULSE_TEXT,
      this._pulseTextOnce,
      options
    );
  }

  /**
   * @param {HTMLElement|Object} ref - DOM element or Vue ref to animate.
   * @param {Object} [options]
   * @param {number} [options.gap] - Duration between shakes in milliseconds.
   * @param {number} [options.count] - Number of buzzes.
   * @param {boolean} [options.infinite] - If true, the animation will repeat infinitely.
   */
  buzz(ref, options) {
    return this.createOngoingAnimation(
      ref,
      this.TYPES.BUZZ,
      this._buzzOnce,
      options
    );
  }

  /**
   * @param {HTMLElement|Object} ref - DOM element or Vue ref to animate.
   * @param {Object} [options]
   * @param {number} [options.gap] - Duration between flashes in milliseconds.
   * @param {number} [options.count] - Number of flashes.
   * @param {boolean} [options.infinite] - If true, the animation will repeat infinitely.
   */
  flash(ref, options) {
    return this.createOngoingAnimation(
      ref,
      this.TYPES.FLASH,
      this._flashOnce,
      options
    );
  }

  /**
   * @param {Array<HTMLElement|Object>} refs - Array of two elements or Vue refs: [outerContainer, innerContainer].
   *   The element to be animated should be nested inside these two containers.
   *   The outer container is used for one axis (e.g., X), and the inner for the other (e.g., Y),
   *   allowing separate CSS transitions for X and Y movements to achieve an arc-like effect.
   * @param {Object} [options]
   * @param {number} [options.duration] - Duration of the animation in milliseconds.
   * @param {HTMLElement|Object} [options.fromRef] - DOM element or Vue ref from which the arc movement originates.
   */
  appearArc(refs, options) {
    const containerRect = document.body.getBoundingClientRect();
    const elementRect = this.getDomElementFromRef(
      refs[0]
    ).getBoundingClientRect();

    let fromDeltaX = containerRect.width / -2;
    let fromDeltaY = 0;

    if (options?.fromRef) {
      const fromElementRect = this.getDomElementFromRef(
        options?.fromRef
      ).getBoundingClientRect();
      const fromElementX = fromElementRect.x + fromElementRect.width / 2;
      const fromElementY = fromElementRect.y + fromElementRect.height / 2;
      fromDeltaX = fromElementX - elementRect.x - elementRect.width / 2;
      fromDeltaY = fromElementY - elementRect.y - elementRect.height / 2;
    }

    let midDeltaY = fromDeltaX / 3;
    if (midDeltaY > fromDeltaY - elementRect.height / 2) {
      midDeltaY = -midDeltaY;
    }
    const duration = options?.duration || 600;

    const methods = [this._appearArcXOnce, this._appearArcYOnce];
    return this.createOngoingSimultaneousAnimationChain(
      refs,
      this.TYPES.APPEAR_ARC,
      methods,
      {
        duration,
        fromDeltaX,
        fromDeltaY,
        midDeltaY,
      }
    );
  }

  createAnimationId(type) {
    return `animation_${type}_${uuidv4().split('-')[0]}`;
  }

  getDomElementFromRef(ref) {
    return ref instanceof Node ? ref : ref?.$el;
  }

  createOngoingAnimation(ref, type, method, options) {
    const element = this.getDomElementFromRef(ref);
    if (!element) {
      return;
    }
    const { count = 1, infinite } = options || {};
    const id = this.createAnimationId(type);
    let stopped = false;
    let stopAtNextPulse = false;
    let rafId = null;
    let timerId = null;

    const cancelPendingTimers = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }
    };

    const scheduleNextPulse = async () => {
      if (stopped || !this.ongoing[id]) {
        return;
      }

      // Increment counter
      if (this.ongoing[id]?.count !== undefined) {
        this.ongoing[id].count++;
      }

      // Check if we've reached the target count
      if (!infinite && this.ongoing[id].count >= count) {
        delete this.ongoing[id];
        return;
      }

      // Check if stop requested after this pulse
      if (stopAtNextPulse) {
        delete this.ongoing[id];
        return;
      }

      // Execute one pulse
      await method.call(this, element, { ...options, id });

      // Schedule next pulse if continuing
      if (!(stopped || stopAtNextPulse)) {
        const gap = options?.gap || 0;
        timerId = setTimeout(() => {
          timerId = null;
          if (!stopped) {
            rafId = requestAnimationFrame(scheduleNextPulse);
          }
        }, gap);
      }
    };

    // Start the animation chain
    rafId = requestAnimationFrame(scheduleNextPulse);

    const animationObj = {
      id,
      type,
      element,
      count: 0,
      promise: Promise.resolve(),
      stopImmediately: () => {
        stopped = true;
        cancelPendingTimers();
        this.removeCssStyles(element, id);
        delete this.ongoing[id];
      },
      stopAtNextPulse: () => {
        stopAtNextPulse = true;
      },
    };

    this.ongoing[id] = animationObj;
    return animationObj;
  }

  createOngoingSimultaneousAnimationChain(refs, type, methods, options) {
    const id = this.createAnimationId(type);
    const elements = refs
      .map((ref) => this.getDomElementFromRef(ref))
      .filter(Boolean);
    if (elements.length === 0) {
      return;
    }

    const animations = methods.map((method, index) => {
      const currentType = `${type}_${index}`;
      return this.createOngoingAnimation(elements[index], currentType, method, {
        ...options,
        id: this.createAnimationId(currentType),
      });
    });

    this.ongoing[id] = {
      id,
      type,
      elements,
      animations,
      count: 0,
      promise: Promise.resolve(),
      stopImmediately: () => {
        for (const animation of animations) {
          animation?.stopImmediately();
        }
        delete this.ongoing[id];
      },
      stopAtNextPulse: () => {
        for (const animation of animations) {
          animation?.stopAtNextPulse();
        }
      },
    };

    return this.ongoing[id];
  }

  _pulseBoxOnce(element, options) {
    const elementStyle = getComputedStyle(element);
    const rgba = colorToRgba(elementStyle.backgroundColor);
    // Type guard: colorToRgba returns undefined for invalid colors or string when format option is passed
    if (!rgba || typeof rgba === 'string') {
      return Promise.resolve();
    }
    const { r, g, b } = rgba;
    const { width, height } = element.getBoundingClientRect();
    const baseSize = Math.min(width, height);
    const shadowSize = this.generateScale(1, 1.4, 3).map((scale) =>
      Math.round(baseSize * (scale - 1))
    );
    const opacity = this.generateScale(0, 0.7, 3);

    return this.applyCssStyles(element, options.id, {
      animation: { duration: 600, gap: options.gap || 0 },
      keyframes: [
        `box-shadow: ${elementStyle.boxShadow}, 0 0 0 ${shadowSize[0]}px rgba(${r},${g},${b},${opacity[2]});`,
        `box-shadow: ${elementStyle.boxShadow}, 0 0 0 ${shadowSize[1]}px rgba(${r},${g},${b}, ${opacity[1]});`,
        `box-shadow: ${elementStyle.boxShadow}, 0 0 0 ${shadowSize[2]}px rgba(${r},${g},${b},${opacity[0]});`,
      ],
    });
  }

  _pulseTextOnce(element, options) {
    const scale = this.generateScale(1, options.scale || 1.8, 3);
    const opacity = this.generateScale(0, 0.8, 3);

    element.setAttribute('data-text', element.textContent);

    return this.applyCssStyles(element, options.id, {
      animation: {
        duration: options.duration || 600,
        applyTo: 'after',
      },
      element: 'position: relative;',
      elementAfter: `
        content: attr(data-text);
        position: absolute;
        top: 0;
        left: 0;
        pointer-events: none;`,
      keyframes: [
        `transform: scale(${scale[0]}); opacity: ${opacity[2]};`,
        `transform: scale(${scale[1]}); opacity: ${opacity[1]};`,
        `transform: scale(${scale[2]}); opacity: ${opacity[0]};`,
      ],
      cloneElementAfter: true,
    }).then(() => {
      element.removeAttribute('data-text');
    });
  }

  _buzzOnce(element, options) {
    const SHAKE_DURATION = 170;
    const SHAKES = 6;
    const elementStyle = getComputedStyle(element);

    const keyframeInitial = `transform: ${elementStyle.transform} rotate(0deg);`;
    const keyframeRotated = `transform: ${elementStyle.transform} rotate(25deg);`;
    const keyframes = new Array(SHAKES)
      .fill()
      .flatMap(() => [keyframeInitial, keyframeRotated]);

    return this.applyCssStyles(element, options.id, {
      animation: {
        duration: SHAKES * SHAKE_DURATION,
        gap: options.gap || 1000,
      },
      element: `transition: transform ${SHAKE_DURATION}ms;`,
      keyframes: [...keyframes, keyframeInitial],
    });
  }

  _flashOnce(element, options) {
    return this.applyCssStyles(element, options.id, {
      animation: {
        duration: 300,
        easing: 'ease-in-out',
        applyTo: 'before',
      },
      element: 'position: relative;',
      elementBefore: `
        content: '';
        position: absolute;
        inset: 0;
        z-index: 20;
        background-color: #FFFFFF;
        pointer-events: none;`,
      keyframes: ['opacity: 0;', 'opacity: 0.8;', 'opacity: 0;'],
    });
  }

  _appearArcXOnce(ref, options) {
    const element = this.getDomElementFromRef(ref);
    const { duration, fromDeltaX } = options;

    return this.applyCssStyles(element, options.id, {
      animation: {
        duration,
        easing: 'ease-in-out',
      },
      keyframes: [
        `transform: translateX(${fromDeltaX}px) scale(0.8); opacity: 0.5;`,
        'transform: translateX(0) scale(1); opacity: 1;',
      ],
    });
  }

  _appearArcYOnce(ref, options) {
    const element = this.getDomElementFromRef(ref);
    const { duration, fromDeltaY, midDeltaY } = options;

    return this.applyCssStyles(element, options.id, {
      animation: {
        duration,
        easing: 'ease-in-out',
      },
      keyframes: [
        `transform: translateY(${fromDeltaY}px);`,
        `transform: translateY(${midDeltaY}px);`,
        'transform: translateY(0);',
      ],
    });
  }

  generateScale(from, to, count) {
    const result = [];
    const stepSize = (to - from) / (count - 1);
    for (let i = 0; i < count; i++) {
      result.push(from + stepSize * i);
    }
    return result;
  }

  applyCssStyles(element, id, rules) {
    const animationStart = performance.now();
    const keyframesSteps = this.generateScale(0, 100, rules.keyframes.length);
    const keyframesStepCode = keyframesSteps.map(
      (percentage, index) => `${percentage}% { ${rules.keyframes[index]} }`
    );
    const keyframesCode = `@keyframes ${id} { ${keyframesStepCode.join('\n')} }`;
    const animationPropCode = `animation: ${id} ${rules.animation.duration}ms ${rules.animation.easing || 'linear'} forwards;`;
    const classCode = `
      .${id} {
        ${rules.element ? this.markCssRulesImportant(rules.element) : ''}
        ${!rules.animation.applyTo ? animationPropCode : ''}
      }
      .${id}::before {
        ${rules.elementBefore ? this.markCssRulesImportant(rules.elementBefore) : ''}
        ${rules.animation.applyTo === 'before' ? animationPropCode : ''}
      }
      .${id}::after {
        ${rules.elementAfter ? this.markCssRulesImportant(rules.elementAfter) : ''}
        ${rules.cloneElementAfter ? this.cloneElementInternalCssCode(element) : ''}
        ${rules.animation.applyTo === 'after' ? animationPropCode : ''}
      }
    `;

    const style = document.createElement('style');
    style.id = id;
    style.textContent = classCode + keyframesCode;
    document.head.appendChild(style);
    element.classList.add(id);

    return new Promise((resolve) => {
      setTimeout(() => {
        const actualDuration = performance.now() - animationStart;
        const expectedDuration = rules.animation.duration;

        // Track animation performance
        this._trackAnimationPerformance(
          id.split('_')[1] || 'unknown',
          expectedDuration,
          actualDuration
        );

        this.removeCssStyles(element, id);
        const waitUntilNext = (rules.animation.gap || 0) + 100;
        setTimeout(() => resolve(), waitUntilNext);
      }, rules.animation.duration);
    });
  }

  removeCssStyles(element, id) {
    const style = document.getElementById(id);
    if (style) {
      style.remove();
    }
    if (element) {
      element.classList.remove(id);
    }
  }

  cloneElementInternalCssCode(element) {
    const allProps = {};
    const cssCodeProps = [];
    const skipProps = ['margin', 'border'];

    for (const sheet of document.styleSheets) {
      try {
        for (const rule of sheet.cssRules) {
          if (rule.type === CSSRule.STYLE_RULE) {
            // Cast to CSSStyleRule after type check to access selectorText and style properties
            /** @type {CSSStyleRule} */
            const styleRule = /** @type {CSSStyleRule} */ (rule);
            if (element.matches(styleRule.selectorText)) {
              for (let i = 0; i < styleRule.style.length; i++) {
                const prop = styleRule.style[i];
                const shouldSkip = skipProps.find((prefix) =>
                  prop.startsWith(prefix)
                );
                const value = styleRule.style.getPropertyValue(prop);
                if (value && value !== 'initial' && !shouldSkip) {
                  allProps[prop] = value;
                }
              }
            }
          }
        }
      } catch (e) {
        // Cross-origin stylesheets may fail - log once per session
        if (!this._cssParseErrorLogged) {
          this._cssParseErrorLogged = true;
          sentryService.addBreadcrumb(
            'animation',
            'CSS rule parsing failed',
            {
              error: e?.message,
            },
            'debug'
          );
        }
      }
    }
    for (const prop in allProps) {
      if (Object.hasOwn(allProps, prop)) {
        cssCodeProps.push(`${prop}:${allProps[prop]}`);
      }
    }
    return `${cssCodeProps.join(';')};`;
  }

  markCssRulesImportant(cssPropsCode) {
    return `${cssPropsCode
      .split(';')
      .map((prop) => prop.trim())
      .filter(Boolean)
      .map((prop) => `${prop} !important`)
      .join(';')};`;
  }

  stopAllAnimations() {
    for (const id in this.ongoing) {
      if (Object.hasOwn(this.ongoing, id)) {
        this.ongoing[id].stopImmediately();
      }
    }

    // Sweep orphan <style> tags from interrupted animations.
    // Animation style IDs always start with "animation_".
    const orphanStyles = document.head.querySelectorAll(
      'style[id^="animation_"]'
    );
    for (const style of orphanStyles) {
      style.remove();
    }
  }
}

export const animations = new Animations();
